import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch } from '../../../redux/store';
import { DBDocumentThunks, DBEntryThunks } from '../../../redux/thunks/DBServiceThunks';
import { TimelineEvent, TimelineEventSource } from '../../../models/TimelineEvent';
import SourceSelector from './source-selector';
import DocumentContent from './document-content';

interface DocumentViewerDialogProps {
  event: TimelineEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DocumentViewerDialog({ event, open, onOpenChange }: DocumentViewerDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [entryBaseOffsets, setEntryBaseOffsets] = useState<Record<string, number>>({});
  const [documentText, setDocumentText] = useState<string>('');
  const [documentName, setDocumentName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Construct sources array including legacy fallback
  const sources: TimelineEventSource[] = event.sources && event.sources.length > 0 
    ? event.sources 
    : (event.sourceText ? [{
        documentId: event.documentId,
        entryId: event.entryId,
        sourceText: event.sourceText,
        startOffset: event.startOffset,
        endOffset: event.endOffset
      }] : []);

  const currentSource = sources[currentSourceIndex];

  useEffect(() => {
    if (!open || !currentSource) return;

    const loadDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch document metadata
        const documentResult = await dispatch(DBDocumentThunks.find({ _id: currentSource.documentId })).unwrap();
        const documents = Array.isArray(documentResult) ? documentResult : documentResult.data || [];
        const document = documents[0];
        setDocumentName(document?.name || t('common.timeline.documentViewer.untitledDocument'));

        // Fetch all entries for this document to reconstruct the full text
        const entries = await dispatch(DBEntryThunks.find({
          documentId: currentSource.documentId,
          $limit: 1000
        })).unwrap();

        const entryArray = Array.isArray(entries) ? entries : entries.data || [];
        
        // Combine all entries into full document text and track offsets
        const sortedEntries = [...entryArray].sort((a: any, b: any) => (a.metadata?.chunkIndex || 0) - (b.metadata?.chunkIndex || 0));
        
        const offsets: Record<string, number> = {};
        let currentOffset = 0;
        
        const fullText = sortedEntries.map((entry: any) => {
          offsets[entry._id] = currentOffset;
          const content = entry.content;
          currentOffset += content.length + 2; // +2 for \n\n
          return content;
        }).join('\n\n');

        setEntryBaseOffsets(offsets);
        setDocumentText(fullText);
        setIsLoading(false);

      } catch (err: any) {
        console.error('Error loading document:', err);
        setError(err.message || t('common.timeline.documentViewer.errorLoading'));
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [open, currentSource?.documentId, dispatch]); // Reload when documentId changes

  // Calculate highlights for all sources in this document
  const highlights = sources
    .filter(s => s.documentId === currentSource?.documentId)
    .map(s => {
      const baseOffset = entryBaseOffsets[s.entryId] || 0;
      return {
        text: s.sourceText,
        absoluteStart: baseOffset + (s.startOffset || 0),
        absoluteEnd: baseOffset + (s.endOffset || 0),
        isFocused: s === currentSource
      };
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {documentName}
          </DialogTitle>
          <DialogDescription>
            {t('common.timeline.documentViewer.viewSource')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            {t('common.timeline.documentViewer.eventLabel')}: {event.title}
          </Badge>
        </div>

        {/* Source Selector */}
        <SourceSelector 
          sources={sources} 
          currentIndex={currentSourceIndex} 
          onSelect={setCurrentSourceIndex} 
        />

        <div className="flex-1 overflow-y-auto border rounded-md p-4 bg-background">
          {isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {!isLoading && !error && currentSource && (
            <DocumentContent 
              documentText={documentText} 
              highlights={highlights}
            />
          )}
        </div>

        <div className="flex justify-between items-center pt-2 text-xs text-muted-foreground">
          <span>
            {t('common.timeline.documentViewer.confidence')}: {Math.round(event.confidenceScore * 100)}%
          </span>
          <span>
            {t('common.timeline.documentViewer.source')}: {event.dateSource}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
